import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import Uploader from 'lib/designSystem/Uploader';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { ExcelRow, parseExcelFile } from './utils';

export { DOCUMENT_SUB_TYPE_MAP } from './utils';

interface BatchImporterProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  suggestions: any[];
  onDocumentsSelected: (documents: { id: string; dueDate?: string }[]) => void;
}

interface ProcessedDocument {
  id: string;
  folio: string;
  businessIdentifier: string;
  businessName: string;
  stakeholderIdentifier: string;
  stakeholderName: string;
  amount: number;
  factoringRequestId: string;
  found: boolean;
  dueDate?: string;
}

const BatchImporter: React.FC<BatchImporterProps> = ({
  open,
  setOpen,
  suggestions,
  onDocumentsSelected,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [foundDocuments, setFoundDocuments] = useState<ProcessedDocument[]>([]);
  const [failedDocuments, setFailedDocuments] = useState<ExcelRow[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);

  const handleFileChange = async (event: any) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    setUploadedFileName(uploadedFile.name);

    if (
      !uploadedFile.name.endsWith('.xlsx') &&
      !uploadedFile.name.endsWith('.xls')
    ) {
      showAlert(
        dispatch,
        'error',
        'Formato de archivo no soportado. Solo .xlsx y .xls',
      );
      return;
    }

    setLoading(true);
    try {
      const parsedData = await parseExcelFile(uploadedFile);
      const processedData = processDocuments(parsedData, suggestions);

      setFoundDocuments(processedData.found);
      setFailedDocuments(processedData.failed);

      if (processedData.found.length === 0) {
        showAlert(
          dispatch,
          'warning',
          'No se encontraron documentos válidos en el archivo',
        );
      }
    } catch (error) {
      console.error('Error processing file:', error);
      showAlert(dispatch, 'error', 'Error al procesar el archivo');
    } finally {
      setLoading(false);
    }
  };

  const processDocuments = (excelData: ExcelRow[], suggestions: any[]) => {
    const found: ProcessedDocument[] = [];
    const failed: ExcelRow[] = [];

    excelData.forEach((row) => {
      if (!row.businessIdentifier || !row.folio) {
        failed.push({
          ...row,
          errorReason: 'Falta RUT cliente o Folio',
        });
        return;
      }

      const matchingSuggestion = suggestions.find(
        (suggestion) =>
          suggestion.businessIdentifier === row.businessIdentifier &&
          suggestion.folio === row.folio &&
          suggestion.subType === row.subType,
      );

      if (!matchingSuggestion) {
        failed.push({
          ...row,
          errorReason: 'Documento no encontrado',
        });
        return;
      }

      found.push({
        ...matchingSuggestion,
        found: true,
        dueDate: row.dueDate,
        stakeholderIdentifier:
          row.stakeholderIdentifier?.trim() ||
          matchingSuggestion.stakeholderIdentifier,
      });
    });

    return { found, failed };
  };

  const handleConfirmSelection = () => {
    if (selectedDocuments.length === 0) {
      showAlert(dispatch, 'warning', 'Debes seleccionar al menos un documento');
      return;
    }

    const selectedWithDueDate = selectedDocuments
      .map((id) => {
        const doc = foundDocuments.find((d) => d.id === id);
        return doc ? { id: doc.id, dueDate: doc.dueDate } : null;
      })
      .filter(Boolean) as { id: string; dueDate?: string }[];

    onDocumentsSelected(selectedWithDueDate);
    setOpen(false);
    setFoundDocuments([]);
    setFailedDocuments([]);
    setSelectedDocuments([]);
    setUploadedFileName(null);
  };

  const handleClose = () => {
    setOpen(false);
    setFoundDocuments([]);
    setFailedDocuments([]);
    setSelectedDocuments([]);
    setUploadedFileName(null);
  };

  const downloadTemplate = () => {
    const template = [
      {
        'Rut cliente': '12345678-9',
        Folio: '123',
        Monto: '100000',
        'Tipo documento': 'Factura electrónica Afecta',
        'Rut Deudor': '98765432-1',
        Vencimiento: '31-12-2025',
      },
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(template);

    const columns = [
      [
        'Rut Cliente',
        'Folio',
        'Monto',
        'Tipo documento',
        'Rut Deudor',
        'Vencimiento',
      ],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, columns, { origin: 'A1' });

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'template_importacion_facturas.xlsx');
  };

  const foundDocumentsHeaders = [
    { key: 'folio', label: 'Folio', type: 'text' },
    { key: 'businessIdentifier', label: 'Rut Cliente', type: 'text' },
    { key: 'businessName', label: 'Nombre Cliente', type: 'text' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor', type: 'text' },
    { key: 'stakeholderName', label: 'Nombre Deudor', type: 'text' },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'dueDate', label: 'Vencimiento', type: 'text' },
    { key: 'factoringRequestId', label: 'Solicitud', type: 'text' },
  ];

  const failedDocumentsHeaders = [
    { key: 'businessIdentifier', label: 'Rut Cliente', type: 'text' },
    { key: 'folio', label: 'Folio', type: 'text' },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'dueDate', label: 'Vencimiento', type: 'text' },
    { key: 'stakeholderName', label: 'Nombre Deudor', type: 'text' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor', type: 'text' },
    { key: 'errorReason', label: 'Razón de Error', type: 'text' },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Importar Facturas desde Excel</Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <Grid container spacing={3}>
            {/* Upload Section */}
            <Grid item xs={12}>
              <Box mb={2}>
                <Typography variant="body1" gutterBottom>
                  Sube un archivo Excel con las facturas que quieres importar.
                  El archivo de la sección "Reportes" funciona como plantilla.
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={downloadTemplate}
                >
                  Descargar plantilla
                </Typography>
              </Box>
              <Uploader
                handleFileChange={handleFileChange}
                buttonText="Subir Excel"
                accept=".xlsx,.xls"
                uploadedFileName={uploadedFileName}
              />
            </Grid>

            {/* Results Section */}
            {foundDocuments.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Se encontraron {foundDocuments.length} documentos válidos
                </Alert>
                <Typography variant="h6" gutterBottom>
                  Documentos Encontrados
                </Typography>
                <EnhancedTable
                  data={foundDocuments}
                  dataHeaders={foundDocumentsHeaders}
                  selected={selectedDocuments}
                  setSelected={setSelectedDocuments}
                  canSelectAll
                />
              </Grid>
            )}

            {failedDocuments.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No se encontraron {failedDocuments.length} documentos en las
                  sugerencias disponibles
                </Alert>
                <Typography variant="h6" gutterBottom>
                  Documentos No Encontrados
                </Typography>
                <EnhancedTable
                  data={failedDocuments}
                  dataHeaders={failedDocumentsHeaders}
                />
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleClose} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleConfirmSelection}
          color="primary"
          disabled={selectedDocuments.length === 0}
        >
          Confirmar Selección ({selectedDocuments.length})
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default BatchImporter;
